import { Button, TextButton } from '@creditornot/cb-button';
import { CaretDown } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { MenuCheckboxItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { FieldConfigurations } from 'apis/product-information-service/types';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { usePluralization } from 'common/hooks';

import ItemRowRenderer from './ItemRowRenderer';
import ProgressTracking from './ProgressTracking';
import SelectAndSearch from './SelectAndSearch';
import useConnectItemsToCatalog from '../useConnectItemsToCatalog';

type Props = {
  items: Item[];
  onClose: () => void;
  onConnectSuccess: () => void;
};

const StyledWoltModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  width: 100%;
  max-width: 640px;
`;

const StyledModaContent = styled(ModalContent)`
  padding: 0;
`;

const InfoContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const InfoAlert = styled(InputAlert)`
  margin-block-end: 8px;
`;

const PropertySelectorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  min-width: 180px;
  padding: 8px 16px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 520px;
  overflow: auto;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledErrorNotification = styled(APIErrorNotification)`
  margin-block-end: 16px;
`;

const SpinnerContainer = styled.div`
  height: 520px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledEmpty = styled(Empty)`
  flex: 1;
`;

const propertyOptions: { value: keyof FieldConfigurations; labelId: string }[] = [
  {
    value: 'name',
    labelId: 'menu-item.name',
  },
  {
    value: 'description',
    labelId: 'menu-item.description',
  },
  {
    value: 'images',
    labelId: 'menu-item.image',
  },
];

const ConnectToCatalogModal = ({ items, onClose, onConnectSuccess }: Props) => {
  const { FormattedPluralMessage } = usePluralization();

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [ignoredProperties, setIgnoredProperties] = useState<(keyof FieldConfigurations)[]>([]);

  const { listData, listingData, isConnecting, error, progressPercentage, connectItemsToCatalog } =
    useConnectItemsToCatalog(items, selectedItemIds, ignoredProperties, onConnectSuccess);

  const visibleData = useMemo(
    () =>
      listData?.filter(
        row =>
          row.name?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase()) || row.gtin?.includes(searchQuery),
      ),
    [listData, searchQuery],
  );

  const handleSelectItemId = (id: string) => {
    setSelectedItemIds(prev => (prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]));
  };

  return (
    <StyledWoltModal isOpen onRequestClose={() => (isConnecting ? undefined : onClose())}>
      <ModalHeader onCloseClick={() => (isConnecting ? undefined : onClose())}>
        <FormattedMessage id="menu.catalog.connect-items-title" />
      </ModalHeader>
      <StyledModaContent>
        {listingData && (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        )}
        {isConnecting && (
          <ProgressTracking percentage={progressPercentage}>
            <FormattedPluralMessage id="menu.catalog.connect-items-progress" count={selectedItemIds.length} />
          </ProgressTracking>
        )}
        {visibleData && !isConnecting && (
          <>
            <InfoContainer>
              <InfoAlert variant="info">
                <FormattedPluralMessage id="menu.catalog.connect-items-info" count={Number(listData?.length)} />
              </InfoAlert>
              <PropertySelectorContainer>
                <LegacyPopover
                  placement="bottom-end"
                  showOnClick
                  showOnHover={false}
                  renderArrow={false}
                  contentZIndex={100002}
                  disableContentPadding
                  content={
                    <StyledMenuItemGroup>
                      {propertyOptions.map(option => (
                        <MenuCheckboxItem
                          key={option.value}
                          onClick={() =>
                            ignoredProperties.includes(option.value)
                              ? setIgnoredProperties(prev => prev.filter(n => n !== option.value))
                              : setIgnoredProperties(prev => [...prev, option.value])
                          }
                          checked={ignoredProperties.includes(option.value)}
                        >
                          <FormattedMessage id={option.labelId} />
                        </MenuCheckboxItem>
                      ))}
                    </StyledMenuItemGroup>
                  }
                >
                  <TextButton variant="brand" size="small" icon={<CaretDown />} iconPosition="end">
                    {ignoredProperties.length === 0 ? (
                      <FormattedMessage id="menu.catalog.select-ignore-properties" />
                    ) : (
                      <FormattedPluralMessage count={ignoredProperties.length} id="menu.catalog.ignored-properties" />
                    )}
                  </TextButton>
                </LegacyPopover>
              </PropertySelectorContainer>
            </InfoContainer>
            <SelectAndSearch
              listData={visibleData}
              searchQuery={searchQuery}
              selectedItemIds={selectedItemIds}
              allowDuplicateGtins
              onChangeSearchQuery={setSearchQuery}
              onSelectItemIds={setSelectedItemIds}
            />
            <ListContainer>
              <AutoSizer disableWidth>
                {({ height }) => (
                  <FixedSizeList
                    height={height}
                    width="100%"
                    itemData={{
                      list: visibleData,
                      allowDuplicateGtins: true,
                      selectedItemIds,
                      onSelectItemId: handleSelectItemId,
                    }}
                    itemCount={visibleData.length}
                    itemSize={76}
                    itemKey={(index, data) => `${data.list[index].gtin}/${index}`}
                    overscanCount={6}
                  >
                    {ItemRowRenderer}
                  </FixedSizeList>
                )}
              </AutoSizer>
              {visibleData.length === 0 && <StyledEmpty />}
            </ListContainer>
          </>
        )}
      </StyledModaContent>
      <StyledModalFooter>
        {!!error && <StyledErrorNotification error={error} />}
        <Actions>
          <Button variant="secondary" size="small" onClick={() => onClose()} disabled={isConnecting}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            style={{ marginInlineStart: 16 }}
            variant="primary"
            size="small"
            disabled={isConnecting || !selectedItemIds.length}
            loading={isConnecting}
            onClick={() => connectItemsToCatalog()}
          >
            <FormattedMessage id="menu.catalog.connect-button" />
          </Button>
        </Actions>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};

export default ConnectToCatalogModal;
